import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAppContext } from "../../AppContext";
import { MyDomain } from "../../App";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { useTranslation } from "react-i18next";

import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyAJ_TmSFOw64uySMXcywxUGJ7BZLeYkqCw",
  authDomain: "doctor12.firebaseapp.com",
  projectId: "doctor12",
  storageBucket: "doctor12.appspot.com",
  messagingSenderId: "727336704178",
  appId: "1:727336704178:web:68a1f491b955a62267d1ab",
};

const app =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const auth = getAuth(app);

function MPHomeLoginWithPhoneNumber({ onLoginSuccess }) {
  const { t } = useTranslation();
  const { setUser } = useAppContext();

  const [authStep, setAuthStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("il");
  const [callingCode, setCallingCode] = useState("+972");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [recaptchaScale, setRecaptchaScale] = useState(1);

  const confirmationResultRef = useRef(null);
  const phoneNumberRef = useRef(phoneNumber);
  const callingCodeRef = useRef(callingCode);

  // Keep refs in sync
  useEffect(() => {
    phoneNumberRef.current = phoneNumber;
  }, [phoneNumber]);
  useEffect(() => {
    callingCodeRef.current = callingCode;
  }, [callingCode]);

  // Calculate scale so reCAPTCHA (304px wide) always fits the screen
  useEffect(() => {
    const calculateScale = () => {
      const screenWidth = window.innerWidth;
      const recaptchaWidth = 304; // Google's fixed reCAPTCHA width
      const padding = 48; // 24px each side
      const available = screenWidth - padding;
      if (available < recaptchaWidth) {
        setRecaptchaScale(available / recaptchaWidth);
      } else {
        setRecaptchaScale(1);
      }
    };
    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, []);

  // Load localStorage user
  useEffect(() => {
    const saved = localStorage.getItem("User");
    if (saved) {
      setUser(JSON.parse(saved));
      if (onLoginSuccess) onLoginSuccess();
    }
  }, []);

  // Init reCAPTCHA once
  useEffect(() => {
    initRecaptcha();
    return () => {
      clearRecaptcha();
    };
  }, []);

  const clearRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {}
      window.recaptchaVerifier = null;
    }
  };

  const initRecaptcha = () => {
    clearRecaptcha();
    window.recaptchaVerifier = new RecaptchaVerifier(
      "recaptcha-container",
      {
        size: "normal",
        callback: () => {
          // User solved reCAPTCHA — fire OTP
          sendOTPAfterRecaptcha();
        },
        "expired-callback": () => {
          setMessage("reCAPTCHA expired. Please solve it again.");
        },
      },
      auth,
    );
    window.recaptchaVerifier.render();
  };

  // Helpers
  const cleanPhone = (phone) => {
    if (!phone) return "";
    return phone[0] === "0" ? phone.slice(1) : phone;
  };

  const buildE164 = (phone, cc) => {
    return (cc || callingCode) + cleanPhone(phone);
  };

  // Fires after reCAPTCHA solved
  const sendOTPAfterRecaptcha = async () => {
    const phone = phoneNumberRef.current;
    const cc = callingCodeRef.current;

    if (!phone || phone.length < 7) {
      setMessage(
        t("PleaseEnterValidPhone") || "Please enter a valid phone number.",
      );
      return;
    }

    setMessage("");
    setLoading(true);

    try {
      const fullPhone = buildE164(phone, cc);
      console.log("Sending OTP to:", fullPhone);

      const result = await signInWithPhoneNumber(
        auth,
        fullPhone,
        window.recaptchaVerifier,
      );
      confirmationResultRef.current = result;
      setMessage((t("OTPSentTo") || "Code sent to") + " " + fullPhone);
      setAuthStep(2);
    } catch (error) {
      console.error("sendOTP error:", error);

      // Reset reCAPTCHA on error
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.render().then((id) => {
          window.grecaptcha?.reset(id);
        });
      }

      if (error.code === "auth/too-many-requests") {
        setMessage("Too many attempts. Please wait.");
      } else if (error.code === "auth/invalid-phone-number") {
        setMessage("Invalid phone number.");
      } else {
        setMessage(error.message || t("ErrorMessage") || "An error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Validate phone before user solves reCAPTCHA
  const validatePhone = () => {
    if (!phoneNumber || phoneNumber.length < 7) {
      setMessage(
        t("PleaseEnterValidPhone") || "Please enter a valid phone number.",
      );
    } else {
      setMessage("");
    }
  };

  // Verify OTP
  const verifyOTP = async () => {
    if (!otp || otp.length < 6) {
      setMessage(t("PleaseEnterValidOTP") || "Please enter the 6-digit code.");
      return;
    }
    if (!confirmationResultRef.current) {
      setMessage("Session expired. Please go back and resend.");
      return;
    }
    setLoading(true);
    try {
      await confirmationResultRef.current.confirm(otp);
      setMessage(t("PhoneVerifiedSuccessfully") || "Phone verified!");
      setAuthStep(3);
    } catch (err) {
      if (err.code === "auth/invalid-verification-code") {
        setMessage("Wrong code. Please try again.");
      } else if (err.code === "auth/code-expired") {
        setMessage("Code expired. Please resend.");
      } else {
        setMessage(err.message || t("InvalidOTP") || "Verification failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend — re-init reCAPTCHA and go back to step 1
  const resendOTP = () => {
    setOtp("");
    setMessage("");
    confirmationResultRef.current = null;
    setAuthStep(1);
    // Small delay so DOM is ready before re-init
    setTimeout(() => initRecaptcha(), 100);
  };

  // Save user (unchanged from your original)
  const saveUserToBackend = async () => {
    if (!userName.trim()) {
      setMessage(t("PleaseEnterYourName") || "Please enter your name.");
      return;
    }
    setLoading(true);
    try {
      let cleaned = phoneNumber;
      if (cleaned[0] === "0") cleaned = cleaned.slice(1);

      const data = {
        CountryNumber: callingCode,
        PhoneNumber: cleaned.startsWith("0") ? cleaned : "0" + cleaned,
        Name: userName,
      };

      const response = await axios.post(
        `${MyDomain}Users2/UserLoginWithPhoneNumber`,
        data,
        { headers: { "Content-Type": "application/json" } },
      );

      localStorage.setItem("User", JSON.stringify(response.data));
      setUser(response.data);
      setMessage(t("LoginSuccessful") || "Login successful!");
      if (onLoginSuccess) setTimeout(() => onLoginSuccess(), 500);
    } catch (err) {
      setMessage(
        (t("FailedToSaveUser") || "Failed. ") +
          (err.response?.data || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhoneEntry = () => {
    setAuthStep(1);
    setOtp("");
    setMessage("");
    confirmationResultRef.current = null;
    setTimeout(() => initRecaptcha(), 100);
  };

  // Shared styles
  const inputStyle = {
    width: "100%",
    height: "52px",
    fontSize: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  };

  const primaryBtn = (disabled) => ({
    width: "100%",
    padding: "16px",
    fontSize: "16px",
    fontWeight: "600",
    border: "none",
    borderRadius: "12px",
    backgroundColor: disabled ? "#d1d5db" : "#667eea",
    color: "#fff",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 4px 12px rgba(102,126,234,0.3)",
    transition: "all 0.2s",
    outline: "none",
  });

  const ghostBtn = (disabled) => ({
    width: "100%",
    padding: "14px",
    fontSize: "14px",
    fontWeight: "600",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    backgroundColor: "#fff",
    color: disabled ? "#9ca3af" : "#667eea",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s",
    outline: "none",
  });

  const isErrorMsg = (msg) =>
    [
      "error",
      "invalid",
      "failed",
      "expired",
      "wrong",
      "attempts",
      "wait",
      "unable",
    ].some((w) => msg.toLowerCase().includes(w));

  const msgBox = (msg) =>
    msg ? (
      <div
        style={{
          padding: "12px 16px",
          borderRadius: "8px",
          backgroundColor: isErrorMsg(msg) ? "#fee2e2" : "#d1fae5",
          color: isErrorMsg(msg) ? "#991b1b" : "#065f46",
          fontSize: "14px",
          textAlign: "center",
          marginBottom: "4px",
        }}
      >
        {msg}
      </div>
    ) : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        width: "100%",
        maxWidth: "500px",
        margin: "0 auto",
      }}
    >
      {/* Progress */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          padding: "16px 0",
          flexWrap: "wrap",
        }}
      >
        {[
          { n: 1, label: t("Phone") || "Phone" },
          { n: 2, label: t("Verify") || "Verify" },
          { n: 3, label: t("Name") || "Name" },
        ].map(({ n, label }, i, arr) => (
          <React.Fragment key={n}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor:
                    authStep === n
                      ? "#667eea"
                      : authStep > n
                        ? "#10b981"
                        : "#e5e7eb",
                  color: authStep >= n ? "#fff" : "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {authStep > n ? "✓" : n}
              </div>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: authStep === n ? "600" : "500",
                  color:
                    authStep === n
                      ? "#667eea"
                      : authStep > n
                        ? "#10b981"
                        : "#9ca3af",
                }}
              >
                {label}
              </span>
            </div>
            {i < arr.length - 1 && (
              <div
                style={{
                  width: 30,
                  height: 2,
                  backgroundColor: authStep > n ? "#10b981" : "#e5e7eb",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── STEP 1 ── */}
      {authStep === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("Welcome")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                margin: 0,
              }}
            >
              {t("EnterPhoneToGetStarted")}
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("PhoneNumber")}
            </label>
            <div
              style={{ display: "flex", gap: "12px", alignItems: "stretch" }}
            >
              <div style={{ minWidth: "110px" }}>
                <PhoneInput
                  country={countryCode}
                  value=""
                  onChange={(value, country) => {
                    setCountryCode(country.countryCode);
                    setCallingCode("+" + country.dialCode);
                  }}
                  enableSearch
                  disableDropdown={false}
                  inputStyle={{ display: "none" }}
                  buttonStyle={{
                    width: "100%",
                    height: "52px",
                    borderRadius: "12px",
                    border: "2px solid #e5e7eb",
                    backgroundColor: "#fff",
                  }}
                  containerStyle={{ width: "100%" }}
                />
                <div
                  style={{
                    marginTop: "6px",
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#667eea",
                  }}
                >
                  {callingCode}
                </div>
              </div>
              <input
                type="tel"
                placeholder="50-123-4567"
                value={phoneNumber}
                onChange={(e) =>
                  setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))
                }
                onBlur={validatePhone}
                style={{ ...inputStyle, flex: 1 }}
                onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                onBlurCapture={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <p
              style={{
                marginTop: "10px",
                fontSize: "14px",
                color: "#6b7280",
                padding: "10px 12px",
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                margin: "10px 0 0",
              }}
            >
              {t("FullNumber") || "Full number:"}{" "}
              <strong style={{ color: "#1a1a1a" }}>
                {buildE164(phoneNumber)}
              </strong>
            </p>
          </div>

          {msgBox(message)}

          {/* ── reCAPTCHA — scaled to fit any screen width ── */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                textAlign: "center",
                margin: 0,
              }}
            >
              {t("SolveRecaptcha") ||
                "Complete the verification below to receive your code"}
            </p>
            {/* Outer wrapper clips to actual rendered size after scaling */}
            <div
              style={{
                width: `${304 * recaptchaScale}px`,
                height: `${78 * recaptchaScale}px`, // reCAPTCHA widget is 78px tall
                overflow: "hidden",
              }}
            >
              {/* Inner div is scaled down — transform-origin top-left keeps alignment */}
              <div
                id="recaptcha-container"
                style={{
                  transformOrigin: "top left",
                  transform: `scale(${recaptchaScale})`,
                }}
              />
            </div>
            <p
              style={{
                fontSize: "11px",
                color: "#9ca3af",
                textAlign: "center",
                margin: 0,
              }}
            >
              Protected by reCAPTCHA ·{" "}
              <a
                href="https://policies.google.com/privacy"
                style={{ color: "#667eea" }}
                target="_blank"
                rel="noreferrer"
              >
                Privacy
              </a>{" "}
              &{" "}
              <a
                href="https://policies.google.com/terms"
                style={{ color: "#667eea" }}
                target="_blank"
                rel="noreferrer"
              >
                Terms
              </a>
            </p>
          </div>

          {loading && (
            <p
              style={{
                textAlign: "center",
                color: "#667eea",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              ⏳ {t("Sending") || "Sending code..."}
            </p>
          )}
        </div>
      )}

      {/* ── STEP 2 ── */}
      {authStep === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("VerifyYourNumber")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                margin: 0,
              }}
            >
              {t("WeSentCodeTo") || "We sent a code to"}{" "}
              <strong>{buildE164(phoneNumber)}</strong>
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("VerificationCode")}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder="• • • • • •"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
              }
              onKeyPress={(e) => e.key === "Enter" && verifyOTP()}
              maxLength={6}
              autoFocus
              style={{
                ...inputStyle,
                fontSize: "22px",
                textAlign: "center",
                letterSpacing: "10px",
                fontWeight: "700",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#667eea")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          {msgBox(message)}

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <button
              onClick={verifyOTP}
              disabled={loading || otp.length < 6}
              style={primaryBtn(loading || otp.length < 6)}
            >
              {loading
                ? t("Verifying") || "Verifying..."
                : t("VerifyCode") || "Verify Code"}
            </button>
            <button
              onClick={resendOTP}
              disabled={loading}
              style={ghostBtn(loading)}
            >
              {t("ResendCode") || "Resend Code"}
            </button>
            <button
              onClick={goBackToPhoneEntry}
              style={{ ...ghostBtn(false), color: "#6b7280" }}
            >
              {t("ChangePhoneNumber") || "Change Phone Number"}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3 ── */}
      {authStep === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("AlmostThere")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                margin: 0,
              }}
            >
              {t("EnterNameToComplete")}
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("YourName")}
            </label>
            <input
              type="text"
              placeholder={t("EnterFullName")}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && saveUserToBackend()}
              autoFocus
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#667eea")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          {msgBox(message)}

          <button
            onClick={saveUserToBackend}
            disabled={loading || !userName.trim()}
            style={primaryBtn(loading || !userName.trim())}
          >
            {loading
              ? t("Completing") || "Completing..."
              : t("CompleteRegistration") || "Complete Registration"}
          </button>
        </div>
      )}
    </div>
  );
}

export default MPHomeLoginWithPhoneNumber;
